// Story 5.8 + Story A.4 (Issue #10) + Issue #17 (B4) — Visual step-progress and timeline indicator for claim status
import type { ComponentType } from 'react';
import {
  Pencil,
  Send,
  UserCheck,
  Wallet,
  CheckCircle2,
  Check,
  XCircle,
  RotateCcw,
  ShieldCheck,
  Briefcase,
  Award,
  FileCheck,
} from 'lucide-react';
import { useClaimTimeline, useExpenses, type Claim } from '../../../hooks/useClaims';
import { cn } from '@rhpc/ui';

type ClaimStatus = Claim['status'];

interface Step {
  key: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
}

const STEPS: Step[] = [
  { key: 'DRAFT',            label: 'Draft',          Icon: Pencil },
  { key: 'SUBMITTED',        label: 'Submitted',       Icon: Send },
  { key: 'ADMIN_APPROVED',   label: 'Admin Review',    Icon: UserCheck },
  { key: 'FINANCE_APPROVED', label: 'Finance Review',  Icon: Wallet },
  { key: 'APPROVED',         label: 'Approved',        Icon: CheckCircle2 },
];

/** Maps status to the step index it sits at (0-based) */
function getStepIndex(status: ClaimStatus): number {
  switch (status) {
    case 'DRAFT':              return 0;
    case 'SUBMITTED':          return 1;
    case 'RETURNED_TO_ADMIN':  return 1;
    case 'ADMIN_APPROVED':     return 2;
    case 'FINANCE_APPROVED':   return 3;
    case 'APPROVED':           return 4;
    case 'REJECTED':           return -1; // special branch
    default:                   return 0;
  }
}

interface ClaimStatusProgressProps {
  claimId?: string;
  status: ClaimStatus;
  rejectionReason?: string | null;
  hardcopyReceived?: boolean;
}

export function ClaimStatusProgress({
  claimId,
  status,
  rejectionReason,
  hardcopyReceived,
}: ClaimStatusProgressProps) {
  const currentIdx = getStepIndex(status);
  const isRejected = status === 'REJECTED';
  const isReturnedToAdmin = status === 'RETURNED_TO_ADMIN';

  const { data: timelineData } = useClaimTimeline(claimId ?? '');
  const { data: expensesData } = useExpenses(claimId ?? '');

  const timeline = timelineData?.timeline ?? [];
  const expenses = expensesData?.expenses ?? [];
  const totalExpenses = expenses.length;

  const adminVerifiedCount = expenses.filter((e) => e.adminVerified && !e.flagReason).length;
  const financeVerifiedCount = expenses.filter((e) => e.financeVerified && !e.flagReason).length;
  const hrVerifiedCount = expenses.filter((e) => e.hrVerified && !e.flagReason).length;

  // Build a lookup map of latest transition to each status
  const stepMetadata: Record<string, { userName?: string | null; createdAt: string }> = {};
  for (const item of timeline) {
    if (!stepMetadata[item.toStatus]) {
      stepMetadata[item.toStatus] = { userName: item.userName, createdAt: item.createdAt };
    }
  }

  return (
    <div className="rounded-xl border border-border bg-surface p-4 sm:p-5 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-text-2 uppercase tracking-wide flex items-center gap-1.5">
          <span>Claim Status & Submission Timeline</span>
          {isReturnedToAdmin && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-warn/10 text-warn border border-warn/20 lowercase first-letter:uppercase">
              <RotateCcw className="h-2.5 w-2.5" /> Returned to Admin
            </span>
          )}
        </h3>
        {timeline.length > 0 && (
          <span className="text-[11px] text-text-2">
            {timeline.length} transition{timeline.length === 1 ? '' : 's'} recorded
          </span>
        )}
      </div>

      {/* Step progress Stepper */}
      <div className="relative flex items-start gap-0 pt-1">
        {STEPS.map((step, idx) => {
          const isCompleted = !isRejected && (idx < currentIdx || (idx === currentIdx && status === 'APPROVED'));
          const isCurrent   = !isRejected && idx === currentIdx && status !== 'APPROVED';
          const StepIcon    = step.Icon;
          const transitionInfo = stepMetadata[step.key];

          return (
            <div key={step.key} className="flex flex-1 flex-col items-center gap-1 relative">
              {/* Connector line (not before first step) */}
              {idx > 0 && (
                <div
                  className={cn(
                    'absolute top-3.5 right-1/2 w-full h-0.5 -translate-y-1/2 transition-colors',
                    isCompleted || (isCurrent && !isRejected)
                      ? 'bg-primary'
                      : 'bg-border'
                  )}
                />
              )}

              {/* Circle icon */}
              <div
                className={cn(
                  'relative z-10 w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all',
                  isCompleted
                    ? 'bg-primary border-primary text-white shadow-xs'
                    : isCurrent
                    ? isReturnedToAdmin
                      ? 'bg-surface border-warn text-warn ring-4 ring-warn/20'
                      : 'bg-surface border-primary text-primary ring-4 ring-primary/20'
                    : 'bg-surface border-muted-foreground/30 text-text-2'
                )}
              >
                {isCompleted ? (
                  <Check className="h-3.5 w-3.5 stroke-[2.5]" />
                ) : (
                  <StepIcon className="h-3.5 w-3.5" />
                )}
              </div>

              {/* Label */}
              <span
                className={cn(
                  'text-[11px] font-semibold text-center leading-tight mt-0.5',
                  isCompleted || isCurrent
                    ? 'text-text'
                    : 'text-text-2'
                )}
              >
                {step.label}
              </span>

              {/* Timestamp & Actor metadata */}
              {transitionInfo && (isCompleted || isCurrent) && (
                <div className="hidden sm:flex flex-col items-center text-[10px] text-text-2 text-center leading-none mt-0.5">
                  {transitionInfo.userName && (
                    <span className="truncate max-w-[85px]" title={transitionInfo.userName}>
                      {transitionInfo.userName.split(' ')[0]}
                    </span>
                  )}
                  <span className="text-[9px] text-text-2/70 mt-0.5">
                    {new Date(transitionInfo.createdAt).toLocaleDateString('en-MY', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Returned to Admin banner */}
      {isReturnedToAdmin && (
        <div className="flex items-start gap-3 rounded-xl bg-warn/10 border border-warn/20 px-4 py-3">
          <RotateCcw className="h-5 w-5 text-warn shrink-0 mt-0.5" />
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-semibold text-warn">Returned to Admin for Review</p>
            <p className="text-xs text-text-2">
              One or more line items were flagged during Finance or HR review. Admin is currently reviewing these entries with the claimant.
            </p>
          </div>
        </div>
      )}

      {/* Rejection banner */}
      {isRejected && (
        <div className="flex items-start gap-3 rounded-xl bg-danger-soft border border-danger/20 px-4 py-3">
          <XCircle className="h-5 w-5 text-danger shrink-0 mt-0.5" />
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-semibold text-danger">Claim Rejected</p>
            {rejectionReason && (
              <p className="break-words text-sm text-danger/80 [overflow-wrap:anywhere]">
                {rejectionReason}
              </p>
            )}
            <p className="text-xs text-text-2">
              You can correct your expenses and resubmit this claim.
            </p>
          </div>
        </div>
      )}

      {/* Per-level Verification Summary Cards */}
      {totalExpenses > 0 && !isRejected && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-1">
          {/* Admin Verification */}
          <div className="rounded-lg border border-border bg-surface/60 p-2.5 space-y-1">
            <div className="flex items-center justify-between text-text-2">
              <span className="text-[11px] font-medium flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5 text-info" /> Admin
              </span>
              {adminVerifiedCount === totalExpenses && (
                <Check className="h-3 w-3 text-ok" />
              )}
            </div>
            <p className="text-xs font-bold text-text">
              {adminVerifiedCount} / {totalExpenses}{' '}
              <span className="text-[10px] font-normal text-text-2">verified</span>
            </p>
          </div>

          {/* Finance Verification */}
          <div className="rounded-lg border border-border bg-surface/60 p-2.5 space-y-1">
            <div className="flex items-center justify-between text-text-2">
              <span className="text-[11px] font-medium flex items-center gap-1">
                <Briefcase className="h-3.5 w-3.5 text-info" /> Finance
              </span>
              {financeVerifiedCount === totalExpenses && (
                <Check className="h-3 w-3 text-ok" />
              )}
            </div>
            <p className="text-xs font-bold text-text">
              {financeVerifiedCount} / {totalExpenses}{' '}
              <span className="text-[10px] font-normal text-text-2">verified</span>
            </p>
          </div>

          {/* HR Verification */}
          <div className="rounded-lg border border-border bg-surface/60 p-2.5 space-y-1">
            <div className="flex items-center justify-between text-text-2">
              <span className="text-[11px] font-medium flex items-center gap-1">
                <Award className="h-3.5 w-3.5 text-ok" /> HR Sign-Off
              </span>
              {hrVerifiedCount === totalExpenses && (
                <Check className="h-3 w-3 text-ok" />
              )}
            </div>
            <p className="text-xs font-bold text-text">
              {hrVerifiedCount} / {totalExpenses}{' '}
              <span className="text-[10px] font-normal text-text-2">verified</span>
            </p>
          </div>

          {/* Hardcopy Status */}
          <div className="rounded-lg border border-border bg-surface/60 p-2.5 space-y-1">
            <div className="flex items-center justify-between text-text-2">
              <span className="text-[11px] font-medium flex items-center gap-1">
                <FileCheck className="h-3.5 w-3.5 text-primary" /> Hardcopy
              </span>
              {hardcopyReceived && (
                <Check className="h-3 w-3 text-ok" />
              )}
            </div>
            <p className={cn('text-xs font-bold', hardcopyReceived ? 'text-ok' : 'text-warn dark:text-amber-400')}>
              {hardcopyReceived ? 'Received' : 'Pending'}
            </p>
          </div>
        </div>
      )}

      {/* Detailed Status History (Issue #10 & CAP-10) */}
      {timeline.length > 0 && (
        <div className="pt-2 border-t border-border space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold text-text-2 uppercase tracking-wide">
              Activity History
            </p>
            <span className="text-[10px] text-text-2">
              {timeline.length} {timeline.length === 1 ? 'event' : 'events'} (latest first)
            </span>
          </div>
          <div className="max-h-[230px] overflow-y-auto pr-1 space-y-2 text-xs divide-y divide-border/40">
            {[...timeline]
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .map((entry) => (
                <div key={entry.id} className="flex items-start justify-between gap-3 pt-2 first:pt-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 font-medium text-text flex-wrap">
                      <span className="font-semibold">
                        {entry.fromStatus.replace(/_/g, ' ')} → {entry.toStatus.replace(/_/g, ' ')}
                      </span>
                      {entry.userName && (
                        <span className="text-text-2 font-normal">by {entry.userName}</span>
                      )}
                    </div>
                    {entry.note && (
                      <p className="text-[11px] text-text-2 italic mt-0.5 break-words">
                        "{entry.note}"
                      </p>
                    )}
                  </div>
                  <span className="text-[10px] text-text-2 shrink-0 whitespace-nowrap pt-0.5 font-mono">
                    {new Date(entry.createdAt).toLocaleDateString('en-MY', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
